import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  useColorModeValue,
  Alert,
  AlertIcon,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { GreshamHeader } from '../components/GreshamHeader';
import { GreshamFooter } from '../components/GreshamFooter';
import { apiGetMySubscription, getStoredAuth } from '../api/client';

const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const { token } = getStoredAuth();
        if (!token) {
          navigate('/member/login', { replace: true });
          return;
        }

        const result = await apiGetMySubscription();
        if (result.success) {
          setSubscription(result.subscription);
        } else {
          setError(result.message || 'Unable to load subscription details.');
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred while loading your subscription.');
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [navigate]);

  const handleGoToArticles = () => {
    navigate('/member/articles');
  };

  const handleGoToDashboard = () => {
    navigate('/member/subscription');
  };

  return (
    <Box minH="100vh" bg={bgColor}>
      <GreshamHeader />

      <Container maxW="4xl" py={12}>
        <VStack spacing={8} align="center">
          <Box
            p={8}
            bg={cardBg}
            borderRadius="xl"
            boxShadow="lg"
            w="full"
            maxW="2xl"
            textAlign="center"
          >
            <VStack spacing={6}>
              {/* Success Icon */}
              <Box
                w="16"
                h="16"
                bg="green.100"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="3xl" color="green.600">✓</Text>
              </Box>

              <Heading size="xl" color="green.600">
                Payment Successful!
              </Heading>

              <Text fontSize="lg" color="gray.600">
                Thank you for your subscription. Your payment has been processed successfully.
              </Text>

              {loading ? (
                <Center py={8}>
                  <VStack spacing={4}>
                    <Spinner size="lg" color="blue.500" />
                    <Text color="gray.600">Loading your subscription details...</Text>
                  </VStack>
                </Center>
              ) : error ? (
                <Alert status="warning" borderRadius="lg">
                  <AlertIcon />
                  {error}
                </Alert>
              ) : subscription ? (
                <Box
                  p={6}
                  bg="blue.50"
                  borderRadius="lg"
                  border="1px"
                  borderColor="blue.200"
                  w="full"
                >
                  <VStack spacing={3} align="start">
                    <Heading size="md" color="blue.800">
                      Subscription Details
                    </Heading>
                    <Text><strong>Plan:</strong> {subscription.plan_name}</Text>
                    <Text><strong>Status:</strong> {subscription.status}</Text>
                    <Text><strong>Amount:</strong> {subscription.currency} {Number(subscription.amount).toFixed(2)}</Text>
                    <Text><strong>Expires:</strong> {subscription.expires_at ? new Date(subscription.expires_at.replace(' ', 'T')).toLocaleDateString() : 'N/A'}</Text>
                    {subscription.days_left && (
                      <Text><strong>Days Remaining:</strong> {subscription.days_left}</Text>
                    )}
                  </VStack>
                </Box>
              ) : (
                <Alert status="info" borderRadius="lg">
                  <AlertIcon />
                  Your subscription is being processed. Please check back in a few moments.
                </Alert>
              )}

              <VStack spacing={4} w="full" pt={4}>
                <Button
                  colorScheme="blue"
                  size="lg"
                  onClick={handleGoToArticles}
                  w="full"
                  maxW="sm"
                >
                  Access Articles
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleGoToDashboard}
                  w="full"
                  maxW="sm"
                >
                  View Subscription Details
                </Button>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <GreshamFooter />
    </Box>
  );
};

export default CheckoutSuccess;